/**
 * User activity tracking utility for auto-logout
 */

export interface ActivityTrackerConfig {
  timeoutMinutes: number;
  onTimeout: () => void;
  events?: string[];
}

export class ActivityTracker {
  private timeoutId: NodeJS.Timeout | null = null;
  private config: ActivityTrackerConfig;
  private isActive: boolean = false;

  constructor(config: ActivityTrackerConfig) {
    this.config = {
      events: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
      ...config
    };
  }

  /**
   * Start tracking user activity
   */
  start(): void {
    if (this.isActive) return;
    
    this.isActive = true;
    this.resetTimer();
    this.addEventListeners();
  }

  /**
   * Stop tracking user activity
   */
  stop(): void {
    if (!this.isActive) return;
    
    this.isActive = false;
    this.clearTimer();
    this.removeEventListeners();
  }

  /**
   * Reset the inactivity timer
   */
  resetTimer(): void {
    this.clearTimer();
    this.timeoutId = setTimeout(() => {
      this.config.onTimeout();
    }, this.config.timeoutMinutes * 60 * 1000);
  }

  /**
   * Clear the current timer
   */
  private clearTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Add event listeners for user activity
   */
  private addEventListeners(): void {
    this.config.events?.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    this.config.events?.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }

  /**
   * Handle user activity
   */
  private handleActivity = (): void => {
    this.resetTimer();
  };

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ActivityTrackerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    if (this.isActive) {
      this.resetTimer();
    }
  }

  /**
   * Get current timeout in minutes
   */
  getTimeoutMinutes(): number {
    return this.config.timeoutMinutes;
  }

  /**
   * Check if tracker is active
   */
  isTracking(): boolean {
    return this.isActive;
  }
}
